import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsApi } from "./api";

const POLL_MS = 45_000;

/** Shared hook for the bell badge and dropdown panel.
 *
 *  - Polls `unread-count` every 45s while the document is visible.
 *  - Refetches on focus and on `document.visibilitychange`.
 *  - Owns the "latest 8" list shown in the dropdown.
 *  - Optimistically updates state on markRead / markAllRead / remove.
 */
export default function useNotifications({ pageSize = 8 } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [arrivedCount, setArrivedCount] = useState(0);
  const pollRef = useRef(null);
  const prevCountRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const d = await notificationsApi.unreadCount();
      const next = d.unread_count ?? 0;
      setUnreadCount(next);
      if (prevCountRef.current !== null && next > prevCountRef.current) {
        setArrivedCount(next - prevCountRef.current);
      }
      prevCountRef.current = next;
    } catch (_) {
      // Network glitch — keep last good count.
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await notificationsApi.list({ limit: pageSize });
      setItems(d.items || []);
      setNextCursor(d.next_cursor || null);
      setUnreadCount(d.unread_count ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const markRead = useCallback(async (id) => {
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
    );
    setUnreadCount((n) => Math.max(0, n - 1));
    try {
      await notificationsApi.markRead(id);
    } catch (_) {
      refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const stamp = new Date().toISOString();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || stamp })));
    setUnreadCount(0);
    try {
      await notificationsApi.markAllRead();
    } catch (_) {
      refresh();
    }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    const target = items.find((n) => n.id === id);
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (target && !target.read_at) {
      setUnreadCount((n) => Math.max(0, n - 1));
    }
    try {
      await notificationsApi.remove(id);
    } catch (_) {
      refresh();
    }
  }, [items, refresh]);

  // Poll while the tab is visible; pause when hidden.
  useEffect(() => {
    const startPoll = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        if (document.visibilityState === "visible") refreshCount();
      }, POLL_MS);
    };
    const stopPoll = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshCount();
        startPoll();
      } else {
        stopPoll();
      }
    };
    refreshCount();
    startPoll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refreshCount);
    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refreshCount);
    };
  }, [refreshCount]);

  const dismissArrived = useCallback(() => setArrivedCount(0), []);

  return {
    unreadCount,
    items,
    nextCursor,
    loading,
    error,
    arrivedCount,
    dismissArrived,
    refresh,
    refreshCount,
    markRead,
    markAllRead,
    remove,
  };
}
